import React from "react";
import { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { translate } from "@/components/IntlProviderWrapper";
import type { CompetenceShortData } from "./LinesDetails";
import { getBackend } from "@/lib/backend/Backend2";
import Select from "react-select";

import type { ILabelValuePair, ILabelValuePairNumber } from "@/lib/Util";
import { isNumber } from "@/lib/Util";
import type { ICompetenceData } from "@/lib/backend/manufacturing2.generated.types";
import { FormattedMessage } from "react-intl";
import { LINE_TYPES } from "@/lib/ManufacturingTags.generated";
import { Link } from "react-router-dom";
import * as Auth from "@/lib/Auth";

const LINE_TYPE_OPTIONS: ILabelValuePair[] = [
    {
        value: LINE_TYPES.ordinary,
        label: translate("Manufacturing.Lines.Types.competence", "Competence"),
    },
];

type Props = {
    show: boolean;
    onHide: () => void;
    onSave: (competence: CompetenceShortData) => Promise<void>;
    child_line_data: CompetenceShortData | null;
    child_competences: Set<number>;
    edit_link: string;
};

const getNumberValidation = (
    input: string,
    min?: number,
    max?: number
): { isValid: boolean; value: number; error: string | null } => {
    const numericValue = parseFloat(input.trim());

    // Check if it's a valid number and within the specified range
    if (
        !isNumber(numericValue) ||
        (min !== undefined && numericValue < min) ||
        (max !== undefined && numericValue > max)
    ) {
        return {
            isValid: false,
            value: 0,
            error: translate("common.error_number", "Number is invalid"),
        };
    }

    return { isValid: true, value: numericValue, error: null };
};

const LinesCreateEditCompetenceModal = (props: Props) => {
    const [competences, setCompetences] = useState<ICompetenceData[]>([]);
    const [line_type, setLineType] = useState<ILabelValuePair | null>(null);
    const [competence_weight, setCompetenceWeight] = useState<string>("");
    const [selected_competence, setSelectedCompetence] = useState<ILabelValuePairNumber | null>(null);
    const [competence_factor, setCompetenceFactor] = useState<string>("");
    const [is_loading, setIsLoading] = useState<boolean>(false);
    const [error_message, setErrorMessage] = useState<string | null>(null);

    const child_line_data = props.child_line_data;
    const can_edit = Auth.isInElevatedRole();
    let edit_link = "";
    if (can_edit && props.edit_link && child_line_data) {
        // TODO - get uuid from child line
        edit_link = `${props.edit_link}${child_line_data.line_uuid}`;
    }

    const resetState = () => {
        setLineType(LINE_TYPE_OPTIONS[0]);
        setSelectedCompetence(null);
        setCompetenceFactor("1");
        setCompetenceWeight("");
        setIsLoading(false);
        setErrorMessage(null);
    };

    const fetchCompetences = async () => {
        setIsLoading(true);
        setErrorMessage(null);

        try {
            const backend = getBackend();
            const { competences } = await backend.manufacturing.getCompetences({});
            let filtered_competences = competences;
            if (props.child_competences.size > 0) {
                filtered_competences = competences.filter(c => !props.child_competences.has(c.id));
            }
            setCompetences(filtered_competences);
        } catch (error) {
            setErrorMessage(translate("common.error", "An error occurred while fetching data"));
        } finally {
            setIsLoading(false);
        }
    };

    const initializeFormData = () => {
        const child_line_data = props.child_line_data;
        if (child_line_data) {
            setSelectedCompetence({
                value: child_line_data.id,
                label: child_line_data.title,
            });
            setCompetenceFactor(child_line_data.factor.toString());
            setCompetenceWeight(child_line_data.weight.toString());
        } else {
            resetState();
        }
    };

    const handleCompetenceTypeChange = (selected_option: ILabelValuePair | null) => {
        setLineType(selected_option);
    };

    const handleCompetenceChange = (selected_option: ILabelValuePairNumber | null) => {
        setSelectedCompetence(selected_option);
    };

    const handleFactorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCompetenceFactor(e.target.value);
    };

    const handleFactorBlur = () => {
        // On blur, if empty, set to "0"
        if (competence_factor.trim() === "") {
            setCompetenceFactor("0");
        }
    };

    const isFormValid = (): boolean => {
        const factor_validation = getNumberValidation(competence_factor, 0.0);
        // Allow empty string for weight (means not set)
        const weight_validation =
            competence_weight.trim() === "" ? { isValid: true, value: -1 } : getNumberValidation(competence_weight, 0);
        return (
            factor_validation.isValid &&
            weight_validation.isValid &&
            (child_line_data || selected_competence !== null) &&
            line_type !== null
        );
    };

    const handleSave = () => {
        if (selected_competence === null) {
            return;
        }

        const validation = isFormValid();
        if (!validation) {
            return;
        }

        const competence_factor_num = getNumberValidation(competence_factor, 0.0).value;
        // If weight is empty, send -1
        const competence_weight_num =
            competence_weight.trim() === "" ? -1 : getNumberValidation(competence_weight, 0).value;

        const competence: CompetenceShortData = {
            id: selected_competence.value,
            title: selected_competence.label,
            factor: competence_factor_num,
            weight: competence_weight_num,
            line_uuid: child_line_data?.line_uuid ?? "",
        };

        props.onSave(competence);
        handleClose();
    };

    const handleClose = () => {
        resetState();
        props.onHide();
    };

    useEffect(() => {
        if (line_type && line_type.value === LINE_TYPES.ordinary && competences.length === 0) {
            fetchCompetences();
        }
    }, [props.child_competences, line_type]);

    useEffect(() => {
        initializeFormData();
    }, [child_line_data]);

    const validation = getNumberValidation(competence_factor);

    if (!props.show) {
        return null;
    }

    return (
        <Modal show={props.show} animation={false} onHide={handleClose} onExited={resetState}>
            <Modal.Header>
                <Modal.Title>
                    {child_line_data
                        ? translate("Manufacturing.Lines.edit_child_line", "Edit child line")
                        : translate("Manufacturing.Lines.add_new_child_line", "Add new child line")}
                </Modal.Title>
                <button type="button" className="close" onClick={handleClose}>
                    <span aria-hidden="true">×</span>
                    <span className="sr-only">{translate("common.close", "Close")}</span>
                </button>
            </Modal.Header>

            <Modal.Body className="p-4">
                {error_message && (
                    <div className="alert alert-danger" role="alert">
                        {error_message}
                    </div>
                )}

                <div className="row align-items-center">
                    <div className="col">
                        <div className="form-row">
                            <div className="col-auto w-100">
                                {!child_line_data && (
                                    <div className="form-group">
                                        <label htmlFor="lineTypeSelect">
                                            {translate("Manufacturing.Lines.Types.type", "Line type")}
                                        </label>
                                        <Select
                                            inputId="lineTypeSelect"
                                            options={LINE_TYPE_OPTIONS}
                                            isClearable
                                            placeholder={translate("common.select_filter_value", "Select value")}
                                            onChange={handleCompetenceTypeChange}
                                            value={line_type}
                                            isLoading={is_loading}
                                            isDisabled={is_loading}
                                            autoFocus
                                        />
                                    </div>
                                )}

                                {((line_type && line_type.value === LINE_TYPES.ordinary) || child_line_data) && (
                                    <>
                                        {child_line_data && (
                                            <div className="form-group">
                                                <label htmlFor="competenceTitle">
                                                    {translate(
                                                        "Manufacturing.People.competence_title",
                                                        "Competence title"
                                                    )}
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="competenceTitle"
                                                    value={child_line_data.title}
                                                    autoFocus={child_line_data !== null}
                                                    readOnly
                                                />
                                            </div>
                                        )}

                                        {!child_line_data && (
                                            <div className="form-group">
                                                <label htmlFor="competenceSelect">
                                                    {translate("Manufacturing.People.competences", "Competences")}
                                                </label>
                                                {competences.length > 0 && (
                                                    <Select
                                                        inputId="competenceSelect"
                                                        options={competences.map(competence => ({
                                                            value: competence.id,
                                                            label: competence.title,
                                                        }))}
                                                        isClearable
                                                        placeholder={translate(
                                                            "common.select_filter_value",
                                                            "Select value"
                                                        )}
                                                        onChange={handleCompetenceChange}
                                                        value={selected_competence}
                                                        isLoading={is_loading}
                                                        isDisabled={is_loading}
                                                    />
                                                )}
                                                {is_loading && (
                                                    <div className="text-muted mt-1">
                                                        <FormattedMessage
                                                            id="common.loading"
                                                            defaultMessage="Loading..."
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        <div className="form-group">
                                            <label htmlFor="competence-factor">
                                                {translate(
                                                    "ShiftAssignmentWeekPool.competence_factor",
                                                    "Competence factor"
                                                )}
                                            </label>
                                            <input
                                                type="number"
                                                className={`form-control ${!validation.isValid ? "is-invalid" : ""}`}
                                                id="competence-factor"
                                                value={competence_factor}
                                                onChange={handleFactorChange}
                                                onBlur={handleFactorBlur}
                                                placeholder="0.1"
                                                disabled={is_loading}
                                            />
                                            {validation.error && (
                                                <div className="invalid-feedback">{validation.error}</div>
                                            )}
                                            <small className="form-text text-muted">
                                                {translate(
                                                    "common.enter_positive_number",
                                                    "Enter a positive number or 0"
                                                )}
                                            </small>
                                        </div>

                                        <div className="form-group mb-0">
                                            <label htmlFor="competenceWeight">
                                                {translate("Manufacturing.Lines.weight", "Weight")}
                                                {!child_line_data && (
                                                    <span
                                                        className="info-icon"
                                                        title={translate(
                                                            "Manufacturing.People.will_copy_weight_from_parent",
                                                            "If weight is not set, it will be copied from parent line"
                                                        )}
                                                    >
                                                        <i className="fas fa-info-circle ml-1"></i>
                                                    </span>
                                                )}
                                            </label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                id="competenceWeight"
                                                value={competence_weight}
                                                onChange={e => setCompetenceWeight(e.target.value)}
                                                placeholder="0"
                                                min="0"
                                                disabled={is_loading}
                                            />
                                            <small className="form-text text-muted">
                                                {translate(
                                                    "Manufacturing.Lines.weight_description",
                                                    "Weight of the competence in the line"
                                                )}
                                            </small>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer>
                {child_line_data !== null && can_edit && (
                    <div className="button-list order-last mr-auto">
                        <Link className="btn btn-outline-primary_ btn-flat-light btn-link_ btn-info_" to={edit_link}>
                            <FormattedMessage id="common.advanced" defaultMessage="Advanced" />
                            ...
                        </Link>
                    </div>
                )}
                <button type="button" className="btn btn-outline-secondary" onClick={handleClose}>
                    {translate("common.cancel", "Cancel")}
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={is_loading || !isFormValid()}
                >
                    {translate("common.save", "Save")}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default LinesCreateEditCompetenceModal;
